import React, {
  PropsWithoutRef,
  RefAttributes,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import InputLabel from "./input-label";
import InputBlockLabel from "./input-block-label";
import InputIcon from "./input-icon";
import InputPassword from "./password";
import { getSizes, getColors } from "./styles";
import { Props, defaultProps } from "./input-props";
import Textarea from "../Textarea";

type NativeAttrs = Omit<React.InputHTMLAttributes<any>, keyof Props>;
export type InputProps = Props & typeof defaultProps & NativeAttrs;

const simulateChangeEvent = (
  el: HTMLInputElement,
  event: React.MouseEvent<HTMLDivElement>
): React.ChangeEvent<HTMLInputElement> => {
  return {
    ...event,
    target: el,
    currentTarget: el,
  };
};

const Input = React.forwardRef<
  HTMLInputElement,
  React.PropsWithChildren<InputProps>
>(
  (
    {
      type,
      label,
      labelRight,
      size,
      status,
      icon,
      iconRight,
      iconClickable,
      onIconClick,
      initialValue,
      onChange,
      name,
      readOnly,
      value,
      onClearClick,
      width,
      className,
      onBlur,
      onFocus,
      autoComplete,
      placeholder,
      children,
      disabled,
      ...props
    },
    ref: React.Ref<HTMLInputElement | null>
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current);

    const [selfValue, setSelfValue] = useState<string>(initialValue);
    const [hover, setHover] = useState<boolean>(false);
    const { height, fontSize, iconClasses } = useMemo(() => getSizes(size), [
      size,
    ]);
    const isControlledComponent = useMemo(() => value !== undefined, [value]);

    const colors = useMemo(() => getColors(status), [status]);

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled || readOnly) return;
      setSelfValue(event.target.value);
      onChange && onChange(event);
    };
    const clearHandler = (event: React.MouseEvent<HTMLDivElement>) => {
      setSelfValue("");
      onClearClick && onClearClick(event);
      /* istanbul ignore next */
      if (!inputRef.current) return;

      const changeEvent = simulateChangeEvent(inputRef.current, event);
      changeEvent.target.value = "";
      onChange && onChange(changeEvent);
      inputRef.current.focus();
    };

    const focusHandler = (e: React.FocusEvent<HTMLInputElement>) => {
      setHover(true);
      onFocus && onFocus(e);
    };
    const blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
      setHover(false);
      onBlur && onBlur(e);
    };

    const iconClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      onIconClick && onIconClick(e);
    };
    const iconProps = useMemo(
      () => ({
        clickable: iconClickable,
        onClick: iconClickHandler,
      }),
      [iconClasses, iconClickable, iconClickHandler]
    );

    useEffect(() => {
      if (isControlledComponent) {
        setSelfValue(value as string);
      }
    });

    const controlledValue = isControlledComponent
      ? { value: selfValue }
      : { defaultValue: initialValue };
    const inputProps = {
      ...props,
      ...controlledValue,
    };

    return (
      <div className="with-label inline-block box-border">
        {children && <InputBlockLabel name={name}>{children}</InputBlockLabel>}
        <div
          className={`input-container ${height} inline-flex items-center ${className}`}
        >
          {label && <InputLabel fontSize={fontSize}>{label}</InputLabel>}
          <div
            className={`input-wrapper box-border inline-flex align-middle items-center h-full flex-1 select-none rounded-md border transition-color duration-200 ease-out ${colors} ${
              disabled
                ? "bg-gray-100 border-gray-200 cursor-not-allowed"
                : "bg-white border-gray-200"
            } ${label ? "rounded-tl-none rounded-bl-none" : ""} ${
              labelRight ? "rounded-tr-none rounded-br-none" : ""
            }`}
          >
            {icon && (
              <InputIcon icon={icon} className={iconClasses} {...iconProps} />
            )}
            <input
              type={type ?? "text"}
              ref={inputRef}
              className={`${fontSize} my-1 mx-3 p-0 shadow-none bg-transparent border-none outline-none focus:ring-0 rounded-none w-full min-w-0 ${
                disabled ? "cursor-not-allowed" : ""
              } ${icon ? "ml-0" : ""} ${iconRight ? "mr-0" : ""}`}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readOnly}
              onFocus={focusHandler}
              onBlur={blurHandler}
              onChange={changeHandler}
              autoComplete={autoComplete}
              id={[name, "input"].join("-")}
              name={name}
              {...inputProps}
            />
            {iconRight && (
              <InputIcon
                icon={iconRight}
                className={iconClasses}
                {...iconProps}
              />
            )}
          </div>
          {labelRight && (
            <InputLabel fontSize={fontSize} isRight={true}>
              {labelRight}
            </InputLabel>
          )}
        </div>
        <style jsx>{`
          .with-label {
            width: ${width};
            -webkit-box-align: center;
          }
          .input-container {
            width: ${width};
          }
        `}</style>
      </div>
    );
  }
);

type InputComponent<T, P = {}> = React.ForwardRefExoticComponent<
  PropsWithoutRef<P> & RefAttributes<T>
> & {
  Textarea: typeof Textarea;
  Password: typeof InputPassword;
};
type ComponentProps = Partial<typeof defaultProps> &
  Omit<Props, keyof typeof defaultProps> &
  NativeAttrs;

Input.defaultProps = defaultProps;

export default Input as InputComponent<HTMLInputElement, ComponentProps>;
